import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";

import type { Payer } from "@medbillai/generated-graphql-types";

import { useDebounced } from "../../../lib/useDebounced";
import { popularPayersQuery, searchPayersQuery } from "./queries";

export type PayerOption = Pick<
  Payer,
  "id" | "primaryPayerId" | "displayName" | "logoUrl" | "memberIdRequired"
>;

const SEARCH_DEBOUNCE_MS = 250;

/** Popular payers until something is typed, then debounced search results. */
export function usePayerSearch({ limit }: { limit: number }) {
  const [searchText, setSearchText] = useState("");
  const query = useDebounced(searchText.trim(), SEARCH_DEBOUNCE_MS);
  const popular = useQuery(popularPayersQuery);
  const search = useQuery(searchPayersQuery, {
    variables: {
      input: {
        query,
        limit,
      },
    },
    skip: query === "",
  });
  const isSearching = searchText.trim() !== "";
  const searchPending = query !== searchText.trim() || search.loading;
  // Keeps the last results on screen while the next query loads.
  const [lastResults, setLastResults] = useState<readonly PayerOption[] | null>(
    null,
  );
  useEffect(() => {
    if (!isSearching) {
      setLastResults(null);
    } else if (!searchPending && search.data) {
      setLastResults(search.data.searchPayers);
    }
  }, [isSearching, searchPending, search.data]);
  const results: readonly PayerOption[] = isSearching
    ? searchPending
      ? (lastResults ?? [])
      : (search.data?.searchPayers ?? [])
    : (popular.data?.popularPayers ?? []);
  // Rows from the previous query stay visible but can't be picked.
  const stale = isSearching && searchPending;
  const loading = isSearching
    ? results.length === 0 && searchPending
    : popular.loading;
  const listError = isSearching ? search.error : popular.error;
  const noResults =
    isSearching && !loading && results.length === 0 && listError == null;
  // Null while typing or not searching.
  const settledSearch =
    isSearching && !searchPending && search.data
      ? { query, resultCount: search.data.searchPayers.length }
      : null;

  return {
    searchText,
    setSearchText,
    isSearching,
    results,
    stale,
    loading,
    listError,
    noResults,
    settledSearch,
  };
}
